import * as React from "react";
import { debounce, get, isUndefined } from "lodash";

import { IControlError, IControlSchema } from "../../core/Controls";

import { Component, createCounterMessage, I18NString, trimString } from "../helpers/Utils";
import { IDispatcher } from "../helpers/Dispatcher";

import Field from "./components/Field";

export interface CompanyProperties {
	name?: string;
	address?: string;
	mailingAddress?: string;
	inn?: string;
	ogrn?: string;
	kpp?: string;
}

export interface IComponentPropertiesSchema extends IControlSchema {
	description: I18NString;
	maxLength?: number;
	minLength?: number;
	disabled?: boolean;
	placeholder?: string;
	defaultValue?: string;
	isInteger?: boolean;
	lines?: number;
	value?: string;
	min?: number;
	max?: number;
	label?: string;
	extra: any;
	data: {
		url: string;
		fill: {
			controlId: string;
			dataPath: string;
		}[];
		method?: "get" | "post";
	};
}

export interface IComponentPropertiesProps {
	schema: IComponentPropertiesSchema;
	api: any;
	dispatcher?: IDispatcher;
	error: IControlError | undefined;
	visited: boolean;
}

export interface SuggestInterface {
	value: string;
	unrestricted_value: string;
	data: {
		address: {
			unrestricted_value: string;
			value: string;
		};
		inn: string;
		kpp: string;
		ogrn: string;
		name: {
			full: string;
			full_with_opf: string;
			short: string;
			short_with_opf: string;
		};
	};
	[key: string]: any;
}

interface IState {
	value: CompanyProperties;
	suggest: SuggestInterface[];
	openInfo: {
		[key: string]: boolean;
	} | null;
	fill: {
		controlId: string;
		dataPath: string;
	}[];
}

const mockData = {
	name: {
		title: "Введите название в свободной форме, ИНН или ОРГН",
		description: undefined,
	},
};

// Components
const Help = ({ onClick }) => (
	<span onClick={onClick} className="input-group-addon btn btn-default">
		<span className="glyphicon glyphicon-question-sign" />
	</span>
);

class ComponentProperties extends Component<IComponentPropertiesProps, IState> {
	state = {
		value: null,
		suggest: null,
		openInfo: null,
		fill: [
			{
				dataPath: "data.name.full",
				controlId: "companyName",
			},
			{
				dataPath: "data.address.value",
				controlId: "companyLocation",
			},
			{
				dataPath: "data.address.unrestricted_value",
				controlId: "companyMailingAddress",
			},
			{
				dataPath: "data.ogrn",
				controlId: "companyOGRN",
			},
			{
				dataPath: "data.inn",
				controlId: "companyINN",
			},
			{
				dataPath: "data.kpp",
				controlId: "companyKPP",
			},
			{
				dataPath: "data.name.full",
				controlId: "agentName",
			},
			{
				dataPath: "data.address.value",
				controlId: "agentLocation",
			},
			{
				dataPath: "data.address.unrestricted_value",
				controlId: "agentMailingAddress",
			},
			{
				dataPath: "data.ogrn",
				controlId: "agentOGRN",
			},
			{
				dataPath: "data.inn",
				controlId: "agentINN",
			},
			{
				dataPath: "data.kpp",
				controlId: "agentKPP",
			},
		],
	};

	constructor(props) {
		super(props);
		this.emitBlur = this.emitBlur.bind(this);
		this.emitSuggest = this.emitSuggest.bind(this);

		// @ts-ignore
		this.emitBlurDebounced = debounce(this.emitBlur, 200);
		// @ts-ignore
		this.emitSuggestDebounced = debounce(this.emitSuggest, 200);
	}

	// lifecycle
	public componentDidMount() {
		this.props.dispatcher.onVisit(this.props.schema.id);
		document.addEventListener("click", this.handlerClickOutside);

		if (this.props.schema?.data?.fill) {
			this.setState({
				fill: this.props.schema.data?.fill,
			});
		}
	}

	// eslint-disable-next-line react/sort-comp
	componentWillUnmount() {
		// @ts-ignore
		this.emitBlurDebounced.cancel();
		// @ts-ignore
		this.emitSuggestDebounced.cancel();
		document.removeEventListener("click", this.handlerClickOutside);
	}

	// handlers
	protected handlerChangeValue = (checkedData: SuggestInterface) => () => {
		// @ts-ignore
		this.emitBlurDebounced(checkedData);

		this.setState({
			suggest: null,
		});
	};

	protected getSuggest = (field) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		event.persist();

		this.setState(({ value }) => ({
			value: {
				...value,
				[field]: trimString(100, event.target.value),
			},
		}));

		// @ts-ignore
		this.emitSuggestDebounced(this.props.schema.id);
	};

	protected emitBlur = (data) => {
		for (const item of this.state?.fill) {
			const value = get(data, item.dataPath, null);
			this.props.dispatcher.onBlur(item.controlId, `${value}`, true, undefined);
		}
	};

	// TODO: Dadata - в большинстве реализаций через GET
	protected emitSuggest = () => {
		const { value } = this.state;
		const { schema, api } = this.props;

		const _method = schema?.data?.method ?? "get";
		const _url = `${api.options.companyUrl}${schema?.data?.url}` ?? `${api.userApi}/suggest`;

		const urlString = _method === "get" ? `${_url}/${value.name}` : `${_url}/`;

		api.moduleApi[_method](
			// TODO: когда везде будет дадата гет запросом - убрать лишние проверки
			urlString,
			{
				query: value.name,
			},
			{
				success: (response) => {
					if (response) {
						this.setState({
							suggest: response.data,
						});
					}
				},
				error: (_error) => {
					this.setState({
						suggest: null,
					});
				},
			}
		);
	};

	protected onClick = (field) => () => {
		this.setState(({ openInfo }) => ({
			openInfo: {
				...openInfo,
				[field]: isUndefined(openInfo?.[field]) ? true : !openInfo[field],
			},
		}));
	};

	public getInput = (
		value: string,
		disabled: boolean,
		maxLength: number,
		minLength: number,
		field: string,
		placeholder: string
	) => {
		const textbox = (
			<input
				type="text"
				className="form-control"
				placeholder={placeholder || mockData[field].title}
				onChange={this.getSuggest(field)}
				value={this.state.value?.[field] ?? value ?? ""}
				title={mockData[field].title}
				disabled={disabled}
			/>
		);

		return mockData?.[field]?.description ? (
			<div style={{ marginBottom: "10px" }}>
				<div className="input-group" style={{ width: "100%", marginBottom: "10px" }}>
					{textbox}
					{!disabled && <Help onClick={this.onClick(field)} />}
				</div>
				{maxLength && (maxLength > 0 || minLength > 0) && (
					<div className="input-counter">
						{createCounterMessage(this.state.value?.[field] ?? value ?? "", maxLength, minLength)}
					</div>
				)}
			</div>
		) : (
			textbox
		);
	};

	handlerClickOutside = (event) => {
		const searchField = document.getElementById("companySearch");
		if (searchField !== event.target) {
			this.setState({
				suggest: null,
				value: null,
			});
		}
	};

	// Render
	public render() {
		const className = "default";

		return (
			<Field {...this.props.schema} status={className}>
				{this.getInput(
					this.state.value?.name,
					this.props.schema.disabled,
					100,
					0,
					"name",
					this.props.schema.placeholder
				)}
				{mockData?.name && mockData?.name?.description && this.state.openInfo?.name && (
					<div
						className="alert alert-warning"
						role="alert"
						dangerouslySetInnerHTML={{
							__html: this.tr(mockData.name.description),
						}}
					/>
				)}

				{this.state.suggest && (
					<>
						<span>Выберите компанию из списка:</span>
						<div className="list-group">
							{this.state.suggest.map((item, index) => (
								<button
									type="button"
									key={index}
									className="list-group-item list-group-item-action"
									onClick={this.handlerChangeValue(item)}
								>
									<strong>{item.data?.name?.full || item.data?.name?.payment || ""}</strong>
									<p>{`${item.data?.inn || ""} ${item.data?.ogrn || ""} ${item.data?.kpp || ""}, ${
										item?.data?.address?.value || ""
									}`}</p>
								</button>
							))}
						</div>
					</>
				)}
			</Field>
		);
	}
}

export function createComponentProperties(
	schema: IControlSchema,
	api: any,
	dispatcher: IDispatcher = null,
	error: IControlError | undefined,
	visited: boolean
): React.ReactElement<any> {
	return (
		<ComponentProperties
			schema={schema as IComponentPropertiesSchema}
			api={api}
			dispatcher={dispatcher}
			error={error}
			visited={visited}
		/>
	);
}
